/**
 * Media Carousel Web Component
 * Handles video reels carousel and Glide.js integration
 */

// Only define if not already exists (prevents duplicate declaration errors)
if (!window.ShMediaCarousel) {

// Debug mode - set to true to enable console logging
const SH_MEDIA_CAROUSEL_DEBUG = false;
const debugLog = (...args) => { if (SH_MEDIA_CAROUSEL_DEBUG) console.log(...args); };
const debugError = (...args) => { if (SH_MEDIA_CAROUSEL_DEBUG) console.error(...args); };
const debugWarn = (...args) => { if (SH_MEDIA_CAROUSEL_DEBUG) console.warn(...args); };

// Configuration constants
const FALLBACK_INIT_DELAY = 3000; // 3 seconds - safety net for initialization
const PLAY_BUTTON_HIDE_DELAY = 800; // Animation duration before hiding play button
const EVENTBUS_CHECK_INTERVAL = 100; // How often to check for EventBus availability

window.ShMediaCarousel = class extends HTMLElement {
  constructor() {
    super();
    this.glideInstance = null;
    this.carouselId = null;
    this.isInitialized = false;
    // Track event listeners for cleanup
    this.eventListeners = new Map();
    // Cache for DOM queries
    this.cache = {
      glideElement: null,
      glideTrack: null
    };
  }

  connectedCallback() {
    // Get settings from data attributes
    this.carouselId = this.dataset.carouselId;
    
    // Wait for EventBus and Glide to be available
    this.waitForGlideInitialization();
    
    // Fallback: Trigger EventBus interaction after delay if events haven't fired
    this.fallbackTimer = setTimeout(() => {
      if (!this.isInitialized) {
        debugLog(`Fallback - triggering EventBus page:interaction for carousel ${this.carouselId}`);
        if (window.EventBus) {
          window.EventBus.emit('page:interaction');
        }
      }
    }, FALLBACK_INIT_DELAY);
  }

  disconnectedCallback() {
    // Pause all videos before cleanup
    this.pauseAllVideos();
    
    // Remove all tracked event listeners
    this.cleanupEventListeners();
    
    // Clear cached DOM queries
    this.clearCache();
    
    // Cleanup when component is removed
    if (this.glideInstance) {
      this.glideInstance.destroy();
    }
    
    // Clear fallback timer
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  }

  /**
   * Cleanup all tracked event listeners to prevent memory leaks
   */
  cleanupEventListeners() {
    this.eventListeners.forEach((listeners, element) => {
      listeners.forEach(({ event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
    });
    this.eventListeners.clear();
    
    // Reset shControlsSetup flags on all slides so they can be re-initialized
    const slides = this.querySelectorAll('.glide__slide');
    slides.forEach(slide => {
      slide.shControlsSetup = false;
    });
  }

  /**
   * Clear cached DOM queries
   */
  clearCache() {
    this.cache.glideElement = null;
    this.cache.glideTrack = null;
  }

  /**
   * Track an event listener for later cleanup
   */
  addTrackedEventListener(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    
    if (!this.eventListeners.has(element)) {
      this.eventListeners.set(element, []);
    }
    this.eventListeners.get(element).push({ event, handler, options });
  }

  /**
   * Setup EventBus listeners for initialization events
   */
  setupEventBusListeners() {
    // Listen for interaction events (first time initialization)
    window.EventBus.on('page:interaction', () => {
      this.initializeCarousel();
    });
    
    // Listen for page change events (reinitialize)
    window.EventBus.on('page:change', () => {
      this.reinitializeCarousel();
    });
    
    // Also listen for direct glide:loaded events
    window.EventBus.on('glide:loaded', () => {
      this.initializeCarousel();
    });
  }

  waitForGlideInitialization() {
    if (window.EventBus) {
      this.setupEventBusListeners();
    } else {
      // Fallback: wait for EventBus to be available
      const checkEventBus = () => {
        if (window.EventBus) {
          this.setupEventBusListeners();
        } else {
          setTimeout(checkEventBus, EVENTBUS_CHECK_INTERVAL);
        }
      };
      checkEventBus();
    }
  }

  initializeCarousel() {
    if (this.isInitialized) {
      debugLog(`Media carousel ${this.carouselId} already initialized, skipping`);
      return;
    }

    debugLog(`Initializing media carousel for section ${this.carouselId}`);

    // Check if Glide is available before trying to use it
    if (typeof Glide === 'undefined') {
      debugLog(`Glide not loaded yet for carousel ${this.carouselId}, waiting...`);
      return; // Exit gracefully, will retry when Glide loads
    }

    debugLog(`Glide is available`);

    const glideSelector = `.js-sh-media-carousel-${this.carouselId}`;
    this.cache.glideElement = this.querySelector(glideSelector);

    if (!this.cache.glideElement) {
      debugError(`Glide element not found with selector: ${glideSelector}`);
      debugLog(`Available elements in component:`, this.querySelectorAll('[class*="glide"]'));
      return;
    }

    debugLog(`Found glide element:`, this.cache.glideElement);
    debugLog(`Creating Glide instance with selector: ${glideSelector}`);

    // Get slide settings from data attributes with defaults
    const slidesDesktop = parseInt(this.dataset.slidesDesktop) || 4;
    const slidesMobile = parseInt(this.dataset.slidesMobile) || 2;

    // Initialize Glide instance
    try {
      this.glideInstance = new Glide(glideSelector, {
      type: 'carousel',
      perView: slidesDesktop,
      startAt: 0,
      dragThreshold: 80,
      swipeThreshold: 80,
      breakpoints: {
        767: { perView: slidesMobile }
      }
    });

      // Add initialized class BEFORE mounting to prevent flash
      this.classList.add('sh-initialized');

      // Setup Glide event listeners
      this.setupGlideEventListeners();

      // Setup video controls (play/pause buttons)
      this.setupVideoControls();

      // Mount Glide
      debugLog(`Mounting Glide instance...`);
      this.glideInstance.mount();

      // Prevent vertical scrolling during horizontal swipe (iOS fix)
      this.preventVerticalScroll();

      this.isInitialized = true;

      debugLog(`Media carousel ${this.carouselId} initialized successfully!`);

      // Clear fallback timer since initialization was successful
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
    } catch (error) {
      debugError(`Error initializing Glide for carousel ${this.carouselId}:`, error);
      debugError(`Error stack:`, error.stack);
    }
  }

  /**
   * Force reinitialize the carousel (useful for page changes)
   */
  reinitializeCarousel() {
    // Remove initialized class during reinitialize
    this.classList.remove('sh-initialized');
    
    // Pause all videos before reinitializing
    this.pauseAllVideos();
    
    // Remove all event listeners
    this.cleanupEventListeners();
    
    // Clear cached DOM queries
    this.clearCache();
    
    // Destroy existing instance
    if (this.glideInstance) {
      this.glideInstance.destroy();
      this.glideInstance = null;
    }
    
    // Reset initialization flag
    this.isInitialized = false;
    
    // Reinitialize (will add sh-initialized class back when done)
    this.initializeCarousel();
  }

  setupGlideEventListeners() {
    if (!this.glideInstance) return;

    debugLog(`Setting up event listeners for carousel ${this.carouselId}`);

    // === MOVE EVENTS ===
    this.glideInstance.on('move.start', () => {
      // Pause all videos when navigation starts
      this.pauseAllVideos();
    });

    // === RUN EVENTS ===
    this.glideInstance.on('run.after', () => {
      // Pause all videos when slide changes
      this.pauseAllVideos();
      this.updatePreviousSlideClass();
      this.handleAutoplay();
    });

    // === MOUNT/BUILD EVENTS ===
    this.glideInstance.on('mount.after', () => {
      // Setup controls for any clones created during mount
      this.setupVideoControls();

      // Keep existing functionality
      this.updatePreviousSlideClass();
      this.handleAutoplay();
    });

    debugLog(`Event listeners setup complete for carousel ${this.carouselId}`);
  }

  /**
   * Prevents vertical page scrolling during horizontal swipe (fixes iOS bug)
   * This is a workaround for Glide.js v3.x issue where horizontal swiping
   * causes unwanted vertical scrolling on mobile devices
   * Updated: Only prevents scroll on the carousel element, not the entire page
   */
  preventVerticalScroll() {
    if (!this.glideInstance) return;

    if (!this.cache.glideTrack) {
      this.cache.glideTrack = this.querySelector('.glide__track');
    }
    if (!this.cache.glideTrack) return;

    let isMoving = false;

    const preventDefault = (e) => {
      // Only prevent if we're actively moving the carousel
      if (isMoving) {
        e.preventDefault();
      }
    };

    // Set flag when carousel starts moving
    this.glideInstance.on(['move'], () => {
      isMoving = true;
    });

    // Clear flag when carousel stops moving
    this.glideInstance.on(['move.after'], () => {
      isMoving = false;
    });

    // Add touch prevention only to the carousel track
    this.addTrackedEventListener(this.cache.glideTrack, 'touchmove', preventDefault, { passive: false });

    debugLog(`Vertical scroll prevention enabled for carousel ${this.carouselId}`);
  }





  /**
   * Updates the previous slide class based on current Glide index
   */
  updatePreviousSlideClass() {
    if (!this.glideInstance) return;

    const currentIndex = this.glideInstance.index;
    
    // Get all slides in DOM order (including clones)
    const allSlides = this.querySelectorAll('.glide__slide');
    
    // Find the active slide in the DOM
    const activeSlide = this.querySelector('.glide__slide--active');
    if (!activeSlide) {
      debugWarn(`No active slide found`);
      return;
    }

    // Get all real slides (not clones) and ensure they have data-slide-index
    const allRealSlides = this.querySelectorAll('.glide__slide:not(.glide__slide--clone)');
    allRealSlides.forEach((slide, index) => {
      if (!slide.hasAttribute('data-slide-index')) {
        slide.setAttribute('data-slide-index', index);
      }
    });

    // Remove previous and next classes from all slides first
    allSlides.forEach(slide => {
      slide.classList.remove('sh-slide--previous');
      slide.classList.remove('sh-slide--next');
    });

    // Find the position of the active slide in the DOM order
    const activeSlideIndex = Array.from(allSlides).indexOf(activeSlide);
    
    // Get the slide that appears immediately before the active slide in DOM order
    const previousSlideInDOM = activeSlideIndex > 0 ? allSlides[activeSlideIndex - 1] : null;
    
    if (previousSlideInDOM) {
      // Add the class to the previous slide (whether it's real or clone)
      previousSlideInDOM.classList.add('sh-slide--previous');
    }
    
    // Get the slide that appears immediately after the active slide in DOM order
    const nextSlideInDOM = activeSlideIndex < allSlides.length - 1 ? allSlides[activeSlideIndex + 1] : null;
    
    if (nextSlideInDOM) {
      // Add the class to the next slide (whether it's real or clone)
      nextSlideInDOM.classList.add('sh-slide--next');
    }
  }

  /**
   * Handles autoplay for the active slide
   * Plays the video in muted state if autoplay is enabled
   */
  handleAutoplay() {
    const activeSlide = this.querySelector('.glide__slide--active');
    if (!activeSlide) return;

    const video = activeSlide.querySelector('video');
    if (!video) return;

    // Check if this video should autoplay
    if (video.dataset.shAutoplay === "true") {
      debugLog(`Autoplay triggered for active slide`);

      // Enforce muted state for autoplay
      video.muted = true;

      // Update mute buttons UI
      const muteBtn = activeSlide.querySelector('.js-sh-mute-video');
      const unmuteBtn = activeSlide.querySelector('.js-sh-unmute-video');
      if (muteBtn) muteBtn.hidden = true;
      if (unmuteBtn) unmuteBtn.hidden = false;

      // Play
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // Update play/pause buttons
          const playBtn = activeSlide.querySelector('.js-sh-play-video');
          const pauseBtn = activeSlide.querySelector('.js-sh-pause-video');
          const controlWrapper = activeSlide.querySelector('.sh-media-carousel__btn--wrapper');
          if (playBtn) {
            playBtn.hidden = true;
            playBtn.classList.add('animate-hide'); // Ensure animation class is present for consistent state
          }
          if (pauseBtn) pauseBtn.hidden = false;
          if (controlWrapper) controlWrapper.hidden = false;
        }).catch(error => {
          debugError("Autoplay prevented:", error);
        });
      }
    }
  }

  /**
   * Setup video play/pause button controls
   */
  setupVideoControls() {
    // Get all slides
    const slides = this.querySelectorAll('.glide__slide');
    
    slides.forEach(slide => {
      // Avoid double-binding listeners using a DOM property (not attribute)
      // Attributes are copied to clones, but properties are not.
      // This ensures clones get listeners attached even if the original had the attribute.
      if (slide.shControlsSetup) return;
      slide.shControlsSetup = true;


      const video = slide.querySelector('video');

      // Handle Autoplay Logic
      if (video && video.hasAttribute('autoplay')) {
        video.dataset.shAutoplay = "true";
        video.removeAttribute('autoplay');
        video.pause();
        video.currentTime = 0;
      }

      const playBtn = slide.querySelector('.js-sh-play-video');
      const pauseBtn = slide.querySelector('.js-sh-pause-video');
      const muteBtn = slide.querySelector('.js-sh-mute-video');
      const unmuteBtn = slide.querySelector('.js-sh-unmute-video');
      const controlWrapper = slide.querySelector('.sh-media-carousel__btn--wrapper');
      
      // Only require play and pause buttons - video may be loaded dynamically
      if (!video || !playBtn || !pauseBtn) return;

      // Helper functions for button visibility
      const animatePlayButtonHide = () => {
        playBtn.classList.add('animate-hide');
        pauseBtn.hidden = false;
        if (controlWrapper) controlWrapper.hidden = false;
        
        // Clear any pending timeout
        if (playBtn.hideTimeout) clearTimeout(playBtn.hideTimeout);
        
        playBtn.hideTimeout = setTimeout(() => {
          if (!video.paused) {
            playBtn.hidden = true;
            playBtn.classList.remove('animate-hide');
          }
        }, PLAY_BUTTON_HIDE_DELAY);
      };
      
      const showPlayButton = () => {
        if (playBtn.hideTimeout) clearTimeout(playBtn.hideTimeout);
        playBtn.classList.remove('animate-hide');
        playBtn.hidden = false;
        pauseBtn.hidden = true;
        if (controlWrapper) controlWrapper.hidden = true;
      };

      // Toggle play/pause on video click
      const videoClickHandler = (e) => {
        e.preventDefault();
        // Note: We don't stopPropagation here to allow swiping
        
        if (video.paused) {
          this.pauseAllVideos();
          video.play().then(() => {
            animatePlayButtonHide();
          }).catch(err => debugError('Error playing video:', err));
        } else {
          video.pause();
          showPlayButton();
        }
      };
      this.addTrackedEventListener(video, 'click', videoClickHandler);
      
      // Initialize button states based on video's current state
      if (muteBtn && unmuteBtn) {
        if (video.muted) {
          muteBtn.hidden = true;
          unmuteBtn.hidden = false;
        } else {
          muteBtn.hidden = false;
          unmuteBtn.hidden = true;
        }
      }
      
      // Helper to prevent Glide from catching button clicks as swipes/slides
      const stopEvent = (e) => { 
        e.stopPropagation();
        if (e.cancelable) {
          // e.preventDefault(); // Optional: might prevent click, be careful
        }
        e.stopImmediatePropagation();
      };
      
      [playBtn, pauseBtn, muteBtn, unmuteBtn].forEach(btn => {
        if (btn) {
          // Stop Glide from seeing these interactions
          // We use capture: true to intercept before bubbling up to Glide wrapper
          // And we listen to pointerdown which Glide likely uses
          const options = { capture: true, passive: false };
          
          this.addTrackedEventListener(btn, 'touchstart', stopEvent, options);
          this.addTrackedEventListener(btn, 'mousedown', stopEvent, options);
          this.addTrackedEventListener(btn, 'pointerdown', stopEvent, options);
          
          // Also need to stop click propagation so it doesn't bubble to slide
          // but we handle the logic in the specific click handlers below
        }
      });

      // Play button click handler
      const playClickHandler = (e) => {
        debugLog('Play button clicked');
        e.preventDefault();
        e.stopPropagation();
        
        // Pause all other videos first
        this.pauseAllVideos();
        
        // Play this video (respects current mute state)
        video.play().then(() => {
          debugLog(`Video playing (${video.muted ? 'muted' : 'unmuted'})`);
          // Toggle play/pause button visibility
          animatePlayButtonHide();
        }).catch(error => {
          debugError('Error playing video:', error);
        });
      };
      this.addTrackedEventListener(playBtn, 'click', playClickHandler);

      // Pause button click handler
      const pauseClickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();

        video.pause();
        // Toggle play/pause button visibility
        showPlayButton();
        debugLog('Video paused');
      };
      this.addTrackedEventListener(pauseBtn, 'click', pauseClickHandler);

      // Mute button click handler
      if (muteBtn) {
        const muteClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();

          video.muted = true;
          // Toggle mute/unmute button visibility
          muteBtn.hidden = true;
          if (unmuteBtn) {
            unmuteBtn.hidden = false;
          }
          debugLog('Video muted');
        };
        this.addTrackedEventListener(muteBtn, 'click', muteClickHandler);
      }

      // Unmute button click handler
      if (unmuteBtn) {
        const unmuteClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();

          video.muted = false;
          // Toggle mute/unmute button visibility
          unmuteBtn.hidden = true;
          if (muteBtn) {
            muteBtn.hidden = false;
          }
          debugLog('Video unmuted');
        };
        this.addTrackedEventListener(unmuteBtn, 'click', unmuteClickHandler);
      }

      // Listen for video ended event
      const videoEndedHandler = () => {
        showPlayButton();
        debugLog('Video ended');
      };
      this.addTrackedEventListener(video, 'ended', videoEndedHandler);
    });

    debugLog('Video controls setup complete');
  }

  /**
   * Pauses all videos in the carousel and resets play/pause buttons
   * Note: Mute state is preserved as it's independent of playback
   */
  pauseAllVideos() {
    const videos = this.querySelectorAll('video');
    const allPlayBtns = this.querySelectorAll('.js-sh-play-video');
    const allPauseBtns = this.querySelectorAll('.js-sh-pause-video');
    
    videos.forEach(video => {
      if (!video.paused) {
        video.pause();
        video.currentTime = 0; // Reset to beginning
      }
      // Note: Mute state is NOT reset - it's independent of playback
    });
    
    // Reset all play/pause buttons to show play button
    allPlayBtns.forEach(btn => {
      btn.hidden = false;
      btn.classList.remove('animate-hide');
      if (btn.hideTimeout) clearTimeout(btn.hideTimeout);
    });
    allPauseBtns.forEach(btn => btn.hidden = true);
    
    // Hide all control wrappers
    const allWrappers = this.querySelectorAll('.sh-media-carousel__btn--wrapper');
    allWrappers.forEach(wrapper => wrapper.hidden = true);
    
    // Note: Mute/unmute buttons are NOT reset - they remain in their current state

    debugLog('All videos paused (mute state preserved)');
  }

};

// Register the custom element if not already registered
if (!customElements.get('sh-media-carousel')) {
  customElements.define('sh-media-carousel', window.ShMediaCarousel);
}

} // End of window.ShMediaCarousel check